import {
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  PASSWORD_COMPLEXITY_DIGIT,
  PASSWORD_COMPLEXITY_LOWERCASE,
  PASSWORD_COMPLEXITY_SPECIAL,
  PASSWORD_COMPLEXITY_UPPERCASE,
  PASSWORD_ERROR_MIXED_CASE,
  PASSWORD_ERROR_NEED_DIGIT,
  PASSWORD_ERROR_NEED_SPECIAL,
  PASSWORD_ERROR_REQUIRED,
  PASSWORD_ERROR_TOO_COMMON,
} from '../constants/password-constants.ts';
import { isCommonWeakPassword } from './weak-passwords.ts';

export { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '../constants/password-constants.ts';

/** Single message when length is outside [{@link MIN_PASSWORD_LENGTH}, {@link MAX_PASSWORD_LENGTH}]. */
export function newPasswordLengthRequirementMessage(): string {
  return `Use between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters.`;
}

export { COMMON_WEAK_PASSWORDS, isCommonWeakPassword } from './weak-passwords.ts';

export function isAdministrativePortalHostname(hostname: string | undefined): boolean {
  if (!hostname) return false;
  const h = hostname.toLowerCase();
  return h.startsWith('admin.') || h.includes('admin.insightia');
}

/**
 * Infer standard vs administrative portal from browser Location (frontend) or Origin/Referer (API).
 */
export function isAdministrativePasswordPolicy(hostname: string | undefined): boolean {
  return isAdministrativePortalHostname(hostname || '');
}

export function validateNewPassword(password: string | null | undefined): { valid: boolean; errors: string[] } {
  if (password == null || typeof password !== 'string') {
    return { valid: false, errors: [PASSWORD_ERROR_REQUIRED] };
  }

  if (password.length > MAX_PASSWORD_LENGTH) {
    return { valid: false, errors: [newPasswordLengthRequirementMessage()] };
  }

  const errors: string[] = [];

  if (password.length < MIN_PASSWORD_LENGTH) errors.push(newPasswordLengthRequirementMessage());
  if (isCommonWeakPassword(password)) errors.push(PASSWORD_ERROR_TOO_COMMON);
  if (!PASSWORD_COMPLEXITY_LOWERCASE.test(password) || !PASSWORD_COMPLEXITY_UPPERCASE.test(password)) {
    errors.push(PASSWORD_ERROR_MIXED_CASE);
  }
  if (!PASSWORD_COMPLEXITY_DIGIT.test(password)) errors.push(PASSWORD_ERROR_NEED_DIGIT);
  if (!PASSWORD_COMPLEXITY_SPECIAL.test(password)) errors.push(PASSWORD_ERROR_NEED_SPECIAL);

  if (errors.length > 0) return { valid: false, errors };
  return { valid: true, errors: [] };
}

export type PasswordPolicyChecks = {
  /** True when password length is within [{@link MIN_PASSWORD_LENGTH}, {@link MAX_PASSWORD_LENGTH}] (inclusive). */
  lengthInRange: boolean;
  notWeak: boolean;
  mixedCase: boolean;
  hasDigit: boolean;
  hasSpecial: boolean;
  minPasswordLength: number;
  maxPasswordLength: number;
  valid: boolean;
  errors: string[];
};

/** For checklist UI on reset/setup password forms. */
export function getPasswordPolicyChecks(password: string | undefined): PasswordPolicyChecks {
  const p = password || '';
  const base = {
    lengthInRange: p.length >= MIN_PASSWORD_LENGTH && p.length <= MAX_PASSWORD_LENGTH,
    notWeak: !isCommonWeakPassword(p),
    mixedCase: PASSWORD_COMPLEXITY_LOWERCASE.test(p) && PASSWORD_COMPLEXITY_UPPERCASE.test(p),
    hasDigit: PASSWORD_COMPLEXITY_DIGIT.test(p),
    hasSpecial: PASSWORD_COMPLEXITY_SPECIAL.test(p),
  };

  const validation = validateNewPassword(p);

  return {
    ...base,
    minPasswordLength: MIN_PASSWORD_LENGTH,
    maxPasswordLength: MAX_PASSWORD_LENGTH,
    valid: validation.valid,
    errors: validation.errors,
  };
}
